import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Package, DollarSign, Calendar } from 'lucide-react';
import { professionalClientService } from '../../../services/professionalClientService';

const AnalyticsTab = ({ clientId }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, [clientId]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const data = await professionalClientService?.getClientAnalytics(clientId);
      setAnalytics(data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        <p className="ml-4 text-gray-600">Chargement des statistiques...</p>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-400" />
        <p className="text-gray-600">Aucune donnée disponible</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-6">Analyse & Statistiques</h2>
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <Package className="w-8 h-8 text-blue-600" />
            <span className="text-2xl font-bold text-blue-900">{analytics?.totalShipments}</span>
          </div>
          <p className="text-sm text-blue-700 font-medium">Total Expéditions</p>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="w-8 h-8 text-green-600" />
            <span className="text-2xl font-bold text-green-900">{analytics?.totalRevenue?.toFixed(0)}€</span>
          </div>
          <p className="text-sm text-green-700 font-medium">Revenu Total</p>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-8 h-8 text-purple-600" />
            <span className="text-2xl font-bold text-purple-900">{analytics?.completedShipments}</span>
          </div>
          <p className="text-sm text-purple-700 font-medium">Expéditions Livrées</p>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <Calendar className="w-8 h-8 text-orange-600" />
            <span className="text-2xl font-bold text-orange-900">{analytics?.averageShipmentsPerMonth}</span>
          </div>
          <p className="text-sm text-orange-700 font-medium">Moy. Expéditions/Mois</p>
        </div>
      </div>
      {/* Documents Stats */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Documents</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center">
            <p className="text-3xl font-bold text-gray-900">{analytics?.totalDocuments}</p>
            <p className="text-sm text-gray-600 mt-1">Total Documents</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-green-600">{analytics?.invoicesCount}</p>
            <p className="text-sm text-gray-600 mt-1">Factures</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-blue-600">{analytics?.ex1Count}</p>
            <p className="text-sm text-gray-600 mt-1">Certificats EX1</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-purple-600">{analytics?.photosCount}</p>
            <p className="text-sm text-gray-600 mt-1">Photos</p>
          </div>
        </div>
      </div>
      {/* Monthly Shipments Chart */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Évolution des Expéditions par Mois</h3>
        <div className="space-y-3">
          {Object.entries(analytics?.monthlyShipments || {})?.map(([month, count]) => (
            <div key={month} className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-700 w-24">{month}</span>
              <div className="flex-1 bg-gray-200 rounded-full h-6 overflow-hidden">
                <div
                  className="bg-green-600 h-full flex items-center justify-end pr-2"
                  style={{ width: `${(count / Math.max(...Object.values(analytics?.monthlyShipments || {}))) * 100}%` }}
                >
                  <span className="text-xs font-medium text-white">{count}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsTab;